import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';

import Loader from '../shared/displayComponents/loader/Loader';
import url from '../constants/config';

import './AdminDashboard.scss';

/* ─── Constants ──────────────────────────────────────────────────── */

const RANGES = [
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
];

/* ─── Diagnostic hint rules (pure functions, easy to extend) ─────── */

/**
 * generateFunnelHints — rule-based diagnostic hints for the engagement funnel.
 *
 * Rules:
 *  1. If (favorites / views) < 10%  → ⚠️  Low favorite rate
 *  2. If views > 0 && favorites = 0  → ℹ️  Viewed but not shortlisted
 *  3. If favorites > 0 && contacts = 0  → 🚨  Favorites not converting
 *
 * Each hint: { level: 'warning'|'alert'|'info', text: string, step: string }
 */
function generateFunnelHints(funnel) {
  const hints = [];
  const views = funnel.viewedPlayers?.totalEvents || 0;
  const favs = funnel.favoritedPlayers?.totalEvents || 0;
  const contacts = funnel.contactedPlayers?.totalEvents || 0;

  if (views > 0 && favs > 0 && favs / views < 0.1) {
    hints.push({
      level: 'warning',
      step: 'favoritedPlayers',
      text: 'Low favorite rate – players may lack strong profiles (videos, evaluation).',
    });
  }

  if (views > 0 && favs === 0) {
    hints.push({
      level: 'info',
      step: 'viewedPlayers',
      text: 'Players are viewed but not shortlisted – check profile quality.',
    });
  }

  if (favs > 0 && contacts === 0) {
    hints.push({
      level: 'alert',
      step: 'favoritedPlayers',
      text: 'Favorites not converting to contacts – coaches may be browsing without intent or missing key info.',
    });
  }

  return hints;
}

/**
 * generateAdminActions — dynamic task suggestions based on dashboard data.
 *
 * Rules (max 5 shown):
 *  1. playersWithNoViews.count > 0   → visibility problem  (with player list)
 *  2. viewedNotFavorited.count > 0   → profile quality     (with player list)
 *  3. activeCoachesNoContact.count>0 → coach engagement     (with coach list)
 *  4. sport supply/demand gap        → recruitment opportunity
 *  5. newPlayers = 0 in range        → supply pipeline dry
 *
 * Each action can carry `players` or `coaches` arrays for drill-down.
 */
function generateAdminActions(data) {
  const actions = [];
  const ad = data.actionData || {};

  const noViews = ad.playersWithNoViews || {};
  if (noViews.count > 0) {
    actions.push({
      id: 'no-views',
      icon: '👻',
      title: `${noViews.count} player${noViews.count !== 1 ? 's' : ''} with no views in last 30 days`,
      why: 'Consider improving visibility or profile completeness.',
      players: noViews.players || [],
    });
  }

  const viewedNF = ad.viewedNotFavorited || {};
  if (viewedNF.count > 0) {
    actions.push({
      id: 'viewed-not-fav',
      icon: '👁️',
      title: `${viewedNF.count} player${viewedNF.count !== 1 ? 's' : ''} viewed but never favorited`,
      why: 'Check profile quality (missing video, GPA, evaluation).',
      players: viewedNF.players || [],
    });
  }

  const noContact = ad.activeCoachesNoContact || {};
  if (noContact.count > 0) {
    actions.push({
      id: 'coaches-no-contact',
      icon: '🤝',
      title: `${noContact.count} active coach${noContact.count !== 1 ? 'es' : ''} with 0 contacts sent`,
      why: 'Coach may need onboarding or support.',
      coaches: noContact.coaches || [],
    });
  }

  // Supply/demand gap — pick sport with biggest gap
  const sports = data.topSearches?.sports || [];
  const gapSport = sports.find(s => s.count >= 10 && s.supply <= 5);
  if (gapSport) {
    actions.push({
      id: 'supply-gap',
      icon: '📊',
      title: `${gapSport.sport} searched ${gapSport.count}×, only ${gapSport.supply} active player${gapSport.supply !== 1 ? 's' : ''}`,
      why: 'High demand / low supply – consider recruiting more players.',
    });
  }

  if (data.newPlayers?.countInRange === 0) {
    actions.push({
      id: 'no-new-players',
      icon: '📭',
      title: 'No new players registered in this period',
      why: 'Supply pipeline may be dry – check recruitment channels.',
    });
  }

  return actions.slice(0, 5);
}

/* ─── Player chip helper (used in action cards) ─────────────────── */

function PlayerChip({ player }) {
  const label = `${player.firstName} ${player.lastName}`;
  const meta = [player.sport, player.graduationYear].filter(Boolean).join(' · ');
  return (
    <Link
      to={`/available-players/${player._id}`}
      className="admin-dash__player-chip"
      title={`${label} (${meta})`}
    >
      <span className="admin-dash__player-chip-name">{label}</span>
      {meta && <span className="admin-dash__player-chip-meta">{meta}</span>}
    </Link>
  );
}

function CoachChip({ email }) {
  return (
    <Link
      to={`/admin/coaches/${encodeURIComponent(email)}`}
      className="admin-dash__coach-chip"
    >
      {email}
    </Link>
  );
}

/* ─── Action Card with expandable player/coach list ─────────────── */

function ActionCard({ action }) {
  const [expanded, setExpanded] = useState(false);
  const hasPlayers = action.players && action.players.length > 0;
  const hasCoaches = action.coaches && action.coaches.length > 0;
  const hasDetails = hasPlayers || hasCoaches;

  return (
    <div className="admin-dash__action-card">
      <span className="admin-dash__action-emoji" role="img" aria-label="action">{action.icon}</span>
      <div className="admin-dash__action-body">
        <span className="admin-dash__action-title">{action.title}</span>
        <span className="admin-dash__action-why">{action.why}</span>
        {hasDetails && (
          <>
            <button
              type="button"
              className="admin-dash__action-toggle"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Hide' : 'Show'} details
              <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
            </button>
            {expanded && (
              <div className="admin-dash__action-details">
                {hasPlayers && action.players.map(p => (
                  <PlayerChip key={p._id} player={p} />
                ))}
                {hasCoaches && action.coaches.map(email => (
                  <CoachChip key={email} email={email} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Trend helpers ──────────────────────────────────────────────── */

function trendPct(current, previous) {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) return { pct: 100, direction: 'up' };
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { pct: 0, direction: 'flat' };
  return { pct: Math.abs(pct), direction: pct > 0 ? 'up' : 'down' };
}

function TrendBadge({ current, previous }) {
  const trend = trendPct(current, previous);
  if (!trend) return <span className="admin-dash__trend admin-dash__trend--flat">–</span>;
  if (trend.direction === 'flat') return <span className="admin-dash__trend admin-dash__trend--flat">0%</span>;
  return (
    <span className={`admin-dash__trend admin-dash__trend--${trend.direction}`}>
      {trend.direction === 'up' ? '↑' : '↓'} {trend.pct}%
    </span>
  );
}

/* ─── Supply/Demand status helper ────────────────────────────────── */

function supplyDemandStatus(demand, supply) {
  if (demand === 0) return { label: 'No searches', color: 'muted' };
  const ratio = supply / demand;
  if (ratio >= 0.5) return { label: 'Healthy', color: 'green' };
  if (supply <= 5 && demand >= 10) return { label: 'High demand / Low supply', color: 'red' };
  if (ratio < 0.2) return { label: 'High demand / Low supply', color: 'red' };
  return { label: 'Low supply', color: 'yellow' };
}

/* ─── Hint icon helper ───────────────────────────────────────────── */

const HINT_ICON = { warning: '⚠️', alert: '🚨', info: 'ℹ️' };

/* ─── Invite Assistant Form ──────────────────────────────────────── */

function InviteAssistantForm() {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteStatus, setInviteStatus] = useState(null); // { type: 'success'|'error', message }
  const [inviteLoading, setInviteLoading] = useState(false);

  const token = localStorage.getItem('authorization');
  const headers = { Authorization: `Bearer ${token}` };

  const handleInvite = async e => {
    e.preventDefault();
    setInviteStatus(null);
    setInviteLoading(true);
    try {
      const res = await axios.post(`${url}/admin/users/invite`, {
        email: inviteEmail,
        firstName: inviteFirstName,
        lastName: inviteLastName,
      }, { headers });
      setInviteStatus({ type: 'success', message: res.data.message || 'Zaproszenie wysłane!' });
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
    } catch (err) {
      setInviteStatus({ type: 'error', message: err.response?.data?.message || 'Błąd wysyłania zaproszenia.' });
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="admin-dash__panel" style={{ marginTop: '24px' }}>
      <h2 className="admin-dash__panel-title">
        <i className="fas fa-user-plus" /> Zaproś asystentkę
      </h2>
      {inviteStatus && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            background: inviteStatus.type === 'success' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${inviteStatus.type === 'success' ? '#86efac' : '#fca5a5'}`,
            color: inviteStatus.type === 'success' ? '#16a34a' : '#dc2626',
          }}
        >
          {inviteStatus.message}
        </div>
      )}
      <form onSubmit={handleInvite} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 180px' }}>
          <label htmlFor="invite-email" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
            Email *
          </label>
          <input
            id="invite-email"
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            required
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label htmlFor="invite-first-name" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
            Imię *
          </label>
          <input
            id="invite-first-name"
            type="text"
            value={inviteFirstName}
            onChange={e => setInviteFirstName(e.target.value)}
            required
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label htmlFor="invite-last-name" style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
            Nazwisko
          </label>
          <input
            id="invite-last-name"
            type="text"
            value={inviteLastName}
            onChange={e => setInviteLastName(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        </div>
        <button
          type="submit"
          disabled={inviteLoading}
          style={{
            padding: '8px 20px',
            background: '#2e496b',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: inviteLoading ? 'not-allowed' : 'pointer',
            opacity: inviteLoading ? 0.7 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {inviteLoading ? 'Wysyłanie...' : 'Wyślij zaproszenie'}
        </button>
      </form>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */

const AdminDashboard = () => {
  const { userRole } = useSelector(({ authorization }) => authorization);
  const history = useHistory();

  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('authorization');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashRes = await axios.get(`${url}/admin/dashboard?range=${range}`, { headers });
      setData(dashRes.data);
    } catch (err) {
      console.error('Dashboard fetch error', err);
      setError(err.response?.data?.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => {
    if (userRole !== 'admin') {
      history.push('/available-players');
      return;
    }
    fetchDashboard();
  }, [fetchDashboard, userRole, history]);

  // Derived diagnostic data
  const funnelHints = useMemo(() => (data ? generateFunnelHints(data.funnel) : []), [data]);
  const adminActions = useMemo(() => (data ? generateAdminActions(data) : []), [data]);

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="admin-dash">
        <div className="admin-dash__error">
          <i className="fas fa-exclamation-triangle" />
          <p>{error}</p>
          <button onClick={fetchDashboard} className="admin-dash__retry">Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const {
    activeCoaches,
    newPlayers,
    contactEvents,
    topSearches,
    funnel,
    previousPeriod,
    generatedAt,
  } = data;

  const funnelSteps = [
    { key: 'viewedPlayers', label: 'Profile Views', icon: 'fa-eye', ...funnel.viewedPlayers, color: '#3b82f6' },
    { key: 'favoritedPlayers', label: 'Favorited', icon: 'fa-heart', ...funnel.favoritedPlayers, color: '#f59e0b' },
    { key: 'contactedPlayers', label: 'Contacted', icon: 'fa-envelope', ...funnel.contactedPlayers, color: '#10b981' },
  ];

  const funnelMax = Math.max(...funnelSteps.map(s => s.totalEvents), 1);

  const prevFunnel = previousPeriod?.funnel || {};

  return (
    <div className="admin-dash">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="admin-dash__header">
        <div>
          <h1 className="admin-dash__title">
            <i className="fas fa-chart-line" /> Admin Dashboard
          </h1>
          <p className="admin-dash__subtitle">
            Platform health &amp; actionable insights
          </p>
        </div>
        <div className="admin-dash__controls">
          <div className="admin-dash__range">
            {RANGES.map(r => (
              <button
                key={r.value}
                type="button"
                className={`admin-dash__range-btn ${range === r.value ? 'admin-dash__range-btn--active' : ''}`}
                onClick={() => setRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" className="admin-dash__refresh" onClick={fetchDashboard}>
            <i className="fas fa-sync-alt" />
          </button>
        </div>
      </div>

      {/* ── Admin Actions ("What should I do today?") ─────────── */}
      {adminActions.length > 0 && (
        <div className="admin-dash__actions">
          <h2 className="admin-dash__actions-title">
            <span className="admin-dash__actions-icon" role="img" aria-label="tools">🔧</span> Admin Actions
            <small className="admin-dash__actions-sub">What should I do today?</small>
          </h2>
          <div className="admin-dash__actions-list">
            {adminActions.map((action) => (
              <ActionCard key={action.id} action={action} />
            ))}
          </div>
        </div>
      )}

      {/* ── KPI Cards ─────────────────────────────────────────── */}
      <div className="admin-dash__cards">
        <div className="admin-dash__card">
          <div className="admin-dash__card-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}>
            <i className="fas fa-users" />
          </div>
          <div className="admin-dash__card-body">
            <span className="admin-dash__card-label">Active Coaches (7d)</span>
            <span className="admin-dash__card-value">{activeCoaches.count7d}</span>
          </div>
        </div>

        <div className="admin-dash__card">
          <div className="admin-dash__card-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>
            <i className="fas fa-users" />
          </div>
          <div className="admin-dash__card-body">
            <span className="admin-dash__card-label">Active Coaches (30d)</span>
            <span className="admin-dash__card-value">{activeCoaches.count30d}</span>
          </div>
        </div>

        <div className="admin-dash__card">
          <div className="admin-dash__card-icon" style={{ background: '#fefce8', color: '#eab308' }}>
            <i className="fas fa-user-plus" />
          </div>
          <div className="admin-dash__card-body">
            <span className="admin-dash__card-label">New Players</span>
            <span className="admin-dash__card-value">
              {newPlayers.countInRange}
              <TrendBadge current={newPlayers.countInRange} previous={previousPeriod?.newPlayers?.countInRange} />
            </span>
          </div>
        </div>

        <div className="admin-dash__card">
          <div className="admin-dash__card-icon" style={{ background: '#fdf2f8', color: '#ec4899' }}>
            <i className="fas fa-envelope" />
          </div>
          <div className="admin-dash__card-body">
            <span className="admin-dash__card-label">Contacts Sent</span>
            <span className="admin-dash__card-value">
              {contactEvents.totalEvents}
              <TrendBadge current={contactEvents.totalEvents} previous={previousPeriod?.contactEvents?.totalEvents} />
              <small className="admin-dash__card-sub">
                {contactEvents.uniquePlayers} unique player{contactEvents.uniquePlayers !== 1 ? 's' : ''}
              </small>
            </span>
          </div>
        </div>
      </div>

      {/* ── Middle row: Funnel + Top Searches ─────────────────── */}
      <div className="admin-dash__row">
        {/* Funnel with diagnostic hints */}
        <div className="admin-dash__panel admin-dash__panel--funnel">
          <h2 className="admin-dash__panel-title">
            <i className="fas fa-filter" /> Engagement Funnel
          </h2>
          <div className="admin-dash__funnel">
            {funnelSteps.map((step, i) => {
              const stepHints = funnelHints.filter(h => h.step === step.key);
              const prevStep = prevFunnel[step.key] || { totalEvents: 0 };
              return (
                <React.Fragment key={step.label}>
                  <div className="admin-dash__funnel-step">
                    <div className="admin-dash__funnel-icon" style={{ color: step.color }}>
                      <i className={`fas ${step.icon}`} />
                    </div>
                    <div className="admin-dash__funnel-data">
                      <span className="admin-dash__funnel-label">{step.label}</span>
                      <span className="admin-dash__funnel-value">
                        {step.totalEvents}
                        <TrendBadge current={step.totalEvents} previous={prevStep.totalEvents} />
                      </span>
                      <span className="admin-dash__funnel-sub">
                        {step.uniquePlayers} unique player{step.uniquePlayers !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="admin-dash__funnel-bar-wrap">
                      <div
                        className="admin-dash__funnel-bar"
                        style={{
                          width: `${Math.round((step.totalEvents / funnelMax) * 100)}%`,
                          background: step.color,
                        }}
                      />
                    </div>
                  </div>
                  {/* Diagnostic hints for this step */}
                  {stepHints.map((hint, hi) => (
                    <div key={hi} className={`admin-dash__funnel-hint admin-dash__funnel-hint--${hint.level}`}>
                      <span className="admin-dash__hint-icon" role="img" aria-label={hint.level}>{HINT_ICON[hint.level]}</span>
                      {hint.text}
                    </div>
                  ))}
                  {i < funnelSteps.length - 1 && (
                    <div className="admin-dash__funnel-arrow">
                      <i className="fas fa-chevron-down" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Top Searches with Supply/Demand */}
        <div className="admin-dash__panel admin-dash__panel--searches">
          <h2 className="admin-dash__panel-title">
            <i className="fas fa-search" /> Top Searches
            <span className="admin-dash__panel-badge">Supply vs Demand</span>
          </h2>
          <div className="admin-dash__searches-grid">
            <div className="admin-dash__search-col">
              <h4 className="admin-dash__search-heading">By Sport</h4>
              {topSearches.sports.length === 0 && (
                <p className="admin-dash__empty-text">No data yet</p>
              )}
              {topSearches.sports.map((s, i) => {
                const status = supplyDemandStatus(s.count, s.supply);
                return (
                  <div key={s.sport} className="admin-dash__rank-row">
                    <span className="admin-dash__rank-pos">{i + 1}</span>
                    <span className="admin-dash__rank-name">{s.sport}</span>
                    <span className="admin-dash__rank-supply">{s.supply}p</span>
                    <span className="admin-dash__rank-count">{s.count}×</span>
                    <span className={`admin-dash__rank-status admin-dash__rank-status--${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="admin-dash__search-col">
              <h4 className="admin-dash__search-heading">By Year</h4>
              {topSearches.years.length === 0 && (
                <p className="admin-dash__empty-text">No data yet</p>
              )}
              {topSearches.years.map((y, i) => {
                const status = supplyDemandStatus(y.count, y.supply);
                return (
                  <div key={y.year} className="admin-dash__rank-row">
                    <span className="admin-dash__rank-pos">{i + 1}</span>
                    <span className="admin-dash__rank-name">{y.year}</span>
                    <span className="admin-dash__rank-supply">{y.supply}p</span>
                    <span className="admin-dash__rank-count">{y.count}×</span>
                    <span className={`admin-dash__rank-status admin-dash__rank-status--${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Invite Assistant ──────────────────────────────────── */}
      <InviteAssistantForm />

      {/* ── Footer timestamp ──────────────────────────────────── */}
      <p className="admin-dash__footer">
        Last updated: {new Date(generatedAt).toLocaleString()}
      </p>
    </div>
  );
};

export default AdminDashboard;
